import React, { useCallback, useEffect, useState } from "react";
import CssBaseline from "@material-ui/core/CssBaseline";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";

import { Web3ReactProvider } from "@web3-react/core";
import { Web3Provider } from "@ethersproject/providers";

import "./i18n";
import {
  baseThemeDark,
  baseThemeLight,
  pulseThemeDark,
  pulseThemeLight,
} from "./theme";

import TopSidebar from "./components/header";
import Home from "./components/home";

import Create from "./components/create/create";
import Staking from "./components/stake/Staking";

import Pools from "./components/pools";
import InternalPools from "./components/internalPools";
import PresaleDetail from "./components/presale";
import Stats from "./components/stats";
import Voting from "./components/voting";
import Admin from "./components/admin";
import Advisory from "./components/advisor";

import {
  CONNECTION_CONNECTED,
  CONFIGURE,
  GET_BALANCES,
  GET_POOLS_SUMMARY,
  THEME_CHANGE_RETURNED,
  GET_GENERALS_INFO,
  GET_STAKING_POOLS_INFO,
  GET_SWAP_INFO,
  THEME_CHANGE,
  THEME_MODE_CHANGE_RETURNED,
} from "./constants";

import Store from "./stores";
import { usePreferedTheme } from "./components/stake/hooks/usePreferedTheme";
import AccountProvider from "./contexts/AccountContext";
import { getThemeFromURL } from "./utils/getThemeFromURL";

const emitter = Store.emitter;
const dispatcher = Store.dispatcher;
const store = Store.store;

function App() {
  const themes = {
    base: {
      light: baseThemeLight,
      dark: baseThemeDark,
    },
    pulse: {
      light: pulseThemeLight,
      dark: pulseThemeDark,
    },
  };
  const [defaultTheme, setDefaultTheme] = useState(
    getThemeFromURL(window.location.hostname)
      ? getThemeFromURL(window.location.hostname)
      : localStorage.getItem("THEME_TYPE") || "base"
  );

  const { preferedThemeMode } = usePreferedTheme();
  const [themeMode, setThemeMode] = useState(preferedThemeMode);

  const location = useLocation();
  const account = store.getStore("account");

  const themeChanged = useCallback(() => {
    setDefaultTheme(store?.getStore("themeType") || "base");
  }, []);

  const themeModeChanged = useCallback((newThemeMode) => {
    setThemeMode(newThemeMode);
  }, []);

  const connectionConnected = useCallback(() => {
    dispatcher.dispatch({ type: GET_STAKING_POOLS_INFO, content: {} });
    dispatcher.dispatch({ type: CONFIGURE, content: {} });
  }, []);

  useEffect(() => {
    setThemeMode(preferedThemeMode);
    emitter.on(CONNECTION_CONNECTED, () => {
      connectionConnected();
    });

    emitter.on(THEME_CHANGE_RETURNED, () => {
      themeChanged();
    });

    emitter.on(THEME_MODE_CHANGE_RETURNED, (e) => {
      themeModeChanged(e);
    });

    return () => {
      emitter.removeListener(CONNECTION_CONNECTED, () => {
        connectionConnected();
      });

      emitter.removeListener(THEME_CHANGE_RETURNED, () => {
        themeChanged();
      });
      emitter.removeListener(THEME_MODE_CHANGE_RETURNED, (e) => {
        themeModeChanged(e);
      });
    };
  }, [themeChanged, themeModeChanged, connectionConnected, preferedThemeMode]);

  useEffect(() => {
    dispatcher.dispatch({ type: GET_BALANCES, content: {} });
    dispatcher.dispatch({ type: GET_POOLS_SUMMARY, content: {} });
    dispatcher.dispatch({ type: GET_GENERALS_INFO, content: {} });
    dispatcher.dispatch({ type: GET_STAKING_POOLS_INFO, content: {} });
    dispatcher.dispatch({ type: GET_SWAP_INFO, content: {} });

    const getSwapInfoTimeoutId = setInterval(() => {
      dispatcher.dispatch({ type: GET_SWAP_INFO, content: {} });
    }, 10000);

    const getBalancesTimeoutId = setInterval(() => {
      dispatcher.dispatch({ type: GET_BALANCES, content: {} });
    }, 10000);
    const getStakingPoolsTimeoutId = setInterval(() => {
      dispatcher.dispatch({ type: GET_STAKING_POOLS_INFO, content: {} });
    }, 10000);
    const getPoolsSummaryTimeoutId = setInterval(() => {
      dispatcher.dispatch({ type: GET_POOLS_SUMMARY, content: {} });
    }, 10000);
    const getGeneralsTimeoutId = setInterval(() => {
      dispatcher.dispatch({ type: GET_GENERALS_INFO, content: {} });
    }, 10000);
    return () => {
      clearInterval(getBalancesTimeoutId);
      clearInterval(getPoolsSummaryTimeoutId);
      clearInterval(getSwapInfoTimeoutId);
      clearInterval(getStakingPoolsTimeoutId);
      clearInterval(getGeneralsTimeoutId);
    };
  }, []);

  useEffect(() => {
    const getStakingPoolsTimeoutId = setInterval(
      () => {
        if (account && account.address) {
          dispatcher.dispatch({ type: GET_STAKING_POOLS_INFO, content: {} });
        }
      },
      account && account.address ? 10000 : 0
    );

    return () => {
      clearInterval(getStakingPoolsTimeoutId);
    };
  }, [account]);

  useEffect(() => {
    if (location) {
      const _theme = getThemeFromURL(window.location.hostname);
      if (_theme && _theme !== store.getStore("themeType")) {
        setDefaultTheme(_theme);
        dispatcher.dispatch({
          type: THEME_CHANGE,
          content: {
            _theme,
          },
        });
      }
    }
  }, [location, themes]);

  const getLibrary = (provider) => {
    const library = new Web3Provider(provider);
    library.pollingInterval = 8000;
    return library;
  };

  return (
    <MuiThemeProvider theme={createMuiTheme(themes[defaultTheme][themeMode])}>
      <Web3ReactProvider getLibrary={getLibrary}>
        <AccountProvider>
          <CssBaseline />
          <TopSidebar>
            <Switch>
              <Route path="/home">
                <Home />
              </Route>
              <Route path="/create">
                <Create />
              </Route>
              <Route path="/staking">
                <Staking />
              </Route>
              <Route path="/pools">
                <Pools />
              </Route>
              <Route path="/pools-abc123xyz">
                <InternalPools />
              </Route>
              <Route path="/stats">
                <Stats />
              </Route>
              <Route path="/pool/:bscsId">
                <PresaleDetail />
              </Route>
              <Route path="/voting">
                <Voting />
              </Route>
              <Route path="/admin">
                <Admin />
              </Route>
              <Route path="/team">
                <Advisory />
              </Route>
              <Redirect to="/home" />
            </Switch>
          </TopSidebar>
        </AccountProvider>
      </Web3ReactProvider>
    </MuiThemeProvider>
  );
}

export default App;
